import React, { useMemo, useState } from 'react'
import { MilestoneStep, MilestoneStepFeedback } from '../../../../types/collaborations/CollaborationTypes'
import moment from 'moment'
import HTMLContent from '../../../HTMLContent'

interface CurrentStepProps {
    currentStep: MilestoneStep,
    setOpenedStep: (slug: string) => void,
    feedbacks: MilestoneStepFeedback[]
    onFeedbackView: (feedback: string) => void,
    addDeadline: () => void,
    refetch: () => void
}


const CurrentStep: React.FC<CurrentStepProps> = ({ currentStep, setOpenedStep, feedbacks, onFeedbackView, addDeadline, refetch }) => {


    const feedback = useMemo(() => {
        const stepFeedbacks = (Array.isArray(feedbacks) ? feedbacks : []).filter(
            (feedback) => feedback.step === currentStep.gig_step.slug
        );

        if (stepFeedbacks.length === 0) return null;

        const sorted = stepFeedbacks.sort(
            (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );

        return sorted[0]; // most recent one
    }, [feedbacks, currentStep]);

    const stepTitle = useMemo(() => {
        const title = (currentStep.gig_step.title ?? (currentStep.gig_step.name ?? "") + "*");
        if (feedback && feedback.revision_required) {
            return currentStep.gig_step.revision_required_title ?? title + "*";
        }

        if (!currentStep.can_process && currentStep.gig_step?.non_assignee_title) {
            return currentStep.gig_step.non_assignee_title;
        }

        return title
    }, [currentStep, feedback])

    return (
        <div className="position-relative">

            <div className={`step-card ${currentStep.can_process ? '' : 'waiting'}`}>
                <div className='step-card-1 d-flex justify-content-between align-items-center'>
                    {
                        currentStep.can_process ?
                            (
                                <div className="next-step-badge">
                                    Next Step
                                </div>
                            ) :
                            (
                                <div className="waiting-badge">
                                    Waiting
                                </div>
                            )
                    }
                    <div className="d-flex align-items-center gap-2 justify-content-end">
                        {
                            !currentStep.can_process &&
                            <button onClick={() => refetch()} className='unset-all text-primary'><i className='fa fa-refresh'></i></button>
                        }
                        {
                            currentStep?.gig_step?.help_url && <a href={currentStep.gig_step.help_url} target="_blank" rel="noopener noreferrer" className='fw-bold d-flex align-items-center gap-1'>
                                <span className=''>
                                    <i className='fa fa-question-circle'></i>
                                </span>
                                <span>
                                    Help
                                </span>
                            </a>
                        }
                    </div>

                </div>
                <div className="step-card-2">
                    <div className='d-flex flex-column'>
                        <span className='gig-step-title text-dark fs-16 fw-600'>{stepTitle}</span>
                        <span className="gig-step-description text-dark-gray fs-12 fw-400">
                            <HTMLContent content={currentStep.gig_step.description ?? ''} />
                        </span>
                    </div>
                    <div className="w-100 d-flex flex-column gap-2">
                        {
                            (feedback) &&
                            <button onClick={() => onFeedbackView(feedback?.text)} className="btn btn-outline-primary fw-600 fs-12">View Feedback</button>
                        }
                        {
                            currentStep.can_process &&
                            <button onClick={() => setOpenedStep(currentStep.gig_step.slug)} className="btn btn-primary">{currentStep.gig_step.cta_text ?? 'Take Action*'}</button>
                        }
                    </div>

                </div>

            </div>
            {
                currentStep.gig_step.require_deadline && !currentStep.deadline &&
                <div className={currentStep.can_process ? 'add-step-deadline' : 'step-deadline'}>
                    <span>Add expected completion date</span>
                    <span className='add-deadline-action text-primary fw-600 fs-12' onClick={() => addDeadline()}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <g clipPath="url(#clip0_635_3228)">
                                <path d="M8.49967 6.00065C8.49967 5.72451 8.27582 5.50065 7.99967 5.50065C7.72353 5.50065 7.49967 5.72451 7.49967 6.00065L7.49967 7.50067H5.99967C5.72353 7.50067 5.49967 7.72453 5.49967 8.00067C5.49967 8.27681 5.72353 8.50067 5.99967 8.50067H7.49967V10.0007C7.49967 10.2768 7.72353 10.5007 7.99967 10.5007C8.27582 10.5007 8.49967 10.2768 8.49967 10.0007L8.49967 8.50067H9.99967C10.2758 8.50067 10.4997 8.27681 10.4997 8.00067C10.4997 7.72452 10.2758 7.50067 9.99967 7.50067H8.49967V6.00065Z" fill="var(--primary)" />
                                <path fillRule="evenodd" clipRule="evenodd" d="M7.99967 0.833984C4.04163 0.833984 0.833008 4.04261 0.833008 8.00065C0.833008 11.9587 4.04163 15.1673 7.99967 15.1673C11.9577 15.1673 15.1663 11.9587 15.1663 8.00065C15.1663 4.04261 11.9577 0.833984 7.99967 0.833984ZM1.83301 8.00065C1.83301 4.5949 4.59392 1.83398 7.99967 1.83398C11.4054 1.83398 14.1663 4.5949 14.1663 8.00065C14.1663 11.4064 11.4054 14.1673 7.99967 14.1673C4.59392 14.1673 1.83301 11.4064 1.83301 8.00065Z" fill="var(--primary)" />
                            </g>
                            <defs>
                                <clipPath id="clip0_635_3228">
                                    <rect width="16" height="16" fill="white" />
                                </clipPath>
                            </defs>
                        </svg>

                        ADD DATE
                    </span>
                </div>
            }
            {
                currentStep.can_process && currentStep.deadline &&
                <div className='add-step-deadline'>
                    <span>Expected completion by {currentStep.deadline} </span>
                </div>
            }
            {
                !currentStep.can_process && currentStep.deadline &&
                <div className='step-deadline'>
                    <span>Expected completion by {currentStep.deadline} </span>
                </div>
            }
        </div>
    )
}

export default CurrentStep